// Medical Scores Database - Complete Collection Part 5
// Additional specialized scores

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Target,
  Shield,
  Zap
} from 'lucide-react';

export const SCORES_PART_5 = {
  // ACT - Asthma Control Test
  act: {
    name: 'Asthma Control Test (ACT)',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Contrôle de l\'asthme (12 ans et plus)',
    color: 'sky',
    fields: [
      { id: 'q1', label: 'Fréquence gêne asthme au travail/école/maison', type: 'select', options: [
        { value: 1, label: '1 - Tout le temps' }, { value: 2, label: '2 - Très souvent' },
        { value: 3, label: '3 - Quelquefois' }, { value: 4, label: '4 - Rarement' }, { value: 5, label: '5 - Jamais' }
      ]},
      { id: 'q2', label: 'Fréquence essoufflement', type: 'select', options: [
        { value: 1, label: '1 - >1x/j' }, { value: 2, label: '2 - 1x/j' },
        { value: 3, label: '3 - 3-6x/sem' }, { value: 4, label: '4 - 1-2x/sem' }, { value: 5, label: '5 - Jamais' }
      ]},
      { id: 'q3', label: 'Fréquence réveils nocturnes', type: 'select', options: [
        { value: 1, label: '1 - ≥4 nuits/sem' }, { value: 2, label: '2 - 2-3 nuits/sem' },
        { value: 3, label: '3 - 1 nuit/sem' }, { value: 4, label: '4 - 1-2x total' }, { value: 5, label: '5 - Jamais' }
      ]},
      { id: 'q4', label: 'Fréquence utilisation B2 de secours', type: 'select', options: [
        { value: 1, label: '1 - ≥3x/j' }, { value: 2, label: '2 - 1-2x/j' },
        { value: 3, label: '3 - 2-3x/sem' }, { value: 4, label: '4 - ≤1x/sem' }, { value: 5, label: '5 - Jamais' }
      ]},
      { id: 'q5', label: 'Auto-évaluation contrôle', type: 'select', options: [
        { value: 1, label: '1 - Pas du tout contrôlé' }, { value: 2, label: '2 - Mal contrôlé' },
        { value: 3, label: '3 - Partiellement contrôlé' }, { value: 4, label: '4 - Bien contrôlé' },
        { value: 5, label: '5 - Totalement contrôlé' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 25 };
    },
    interpret: (score) => {
      if (score >= 20) return { level: 'success', text: `ACT ${score}/25`, detail: 'Asthme bien contrôlé' };
      if (score >= 16) return { level: 'warning', text: `ACT ${score}/25`, detail: 'Asthme partiellement contrôlé - Ajustement thérapeutique à discuter' };
      return { level: 'danger', text: `ACT ${score}/25`, detail: 'Asthme non contrôlé - Intensification traitement' };
    },
    formula: 'ACT: 5 questions (1-5). ≥20 = contrôlé, <20 = non contrôlé'
  },

  // Apgar chirurgical
  apgarChirurgical: {
    name: 'Score Apgar Chirurgical',
    category: 'Chirurgie',
    icon: Bone,
    description: 'Sévérité per-opératoire',
    color: 'stone',
    fields: [
      { id: 'bloodLoss', label: 'Pertes sanguines estimées (mL)', type: 'select', options: [
        { value: 0, label: '> 1000 mL (0 pt)' },
        { value: 1, label: '601-1000 mL (1 pt)' },
        { value: 2, label: '101-600 mL (2 pts)' },
        { value: 3, label: '≤ 100 mL (3 pts)' }
      ]},
      { id: 'lowestMAP', label: 'PAM la plus basse (mmHg)', type: 'select', options: [
        { value: 0, label: '< 40 (0 pt)' },
        { value: 1, label: '40-54 (1 pt)' },
        { value: 2, label: '55-69 (2 pts)' },
        { value: 3, label: '≥ 70 (3 pts)' }
      ]},
      { id: 'lowestHR', label: 'FC la plus basse (bpm)', type: 'select', options: [
        { value: 0, label: '> 85 (0 pt)' },
        { value: 1, label: '76-85 (1 pt)' },
        { value: 2, label: '66-75 (2 pts)' },
        { value: 3, label: '56-65 (3 pts)' },
        { value: 4, label: '≤ 55 (4 pts)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score >= 8) return { level: 'success', text: `Apgar chir. ${score}/10`, detail: 'Faible risque complication majeure' };
      if (score >= 5) return { level: 'warning', text: `Apgar chir. ${score}/10`, detail: 'Risque modéré - Surveillance rapprochée' };
      return { level: 'danger', text: `Apgar chir. ${score}/10`, detail: 'Haut risque complication/décès 30j - Soins intensifs' };
    },
    formula: 'Apgar chirurgical = Pertes sang + PAM + FC'
  },

  // Aquitain TVP
  aquitain: {
    name: 'Score Aquitain (TVP)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Probabilité TVP distale vs proximale',
    color: 'rose',
    fields: [
      { id: 'surgery', label: 'Chirurgie < 4 sem ou immobilisation > 3j', type: 'checkbox', points: 1 },
      { id: 'cancer', label: 'Cancer actif ou < 6 mois', type: 'checkbox', points: 1 },
      { id: 'paralysis', label: 'Paralysie/Parésie/Plâtre MI', type: 'checkbox', points: 1 },
      { id: 'tenderness', label: 'Douleur trajet veineux', type: 'checkbox', points: 1 },
      { id: 'edema', label: 'Œdème mollet > 3cm vs côté sain', type: 'checkbox', points: 1 },
      { id: 'pitting', label: 'Œdème prenant le godet', type: 'checkbox', points: 1 },
      { id: 'collateral', label: 'Veines collatérales superficielles', type: 'checkbox', points: 1 },
      { id: 'atcd', label: 'ATCD TVP documenté', type: 'checkbox', points: 1 },
      { id: 'alternative', label: 'Diagnostic alternatif au moins aussi probable', type: 'checkbox', points: -2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.surgery) total += 1;
      if (values.cancer) total += 1;
      if (values.paralysis) total += 1;
      if (values.tenderness) total += 1;
      if (values.edema) total += 1;
      if (values.pitting) total += 1;
      if (values.collateral) total += 1;
      if (values.atcd) total += 1;
      if (values.alternative) total -= 2;
      return { score: total, max: 8, min: -2 };
    },
    interpret: (score) => {
      if (score <= 0) return { level: 'success', text: `Aquitain ${score}`, detail: 'TVP peu probable - D-dimères' };
      if (score <= 2) return { level: 'warning', text: `Aquitain ${score}`, detail: 'TVP distale probable - Écho-Doppler veineux' };
      return { level: 'danger', text: `Aquitain ${score}`, detail: 'TVP proximale probable - Écho-Doppler urgent' };
    },
    formula: 'Aquitain: variante du Wells pour localisation TVP'
  },

  // BODE Index - BPCO
  bode: {
    name: 'Index BODE',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Pronostic BPCO',
    color: 'sky',
    fields: [
      { id: 'fev1', label: 'VEMS (% prédit)', type: 'select', options: [
        { value: 0, label: '≥ 65% (0 pt)' },
        { value: 1, label: '50-64% (1 pt)' },
        { value: 2, label: '36-49% (2 pts)' },
        { value: 3, label: '≤ 35% (3 pts)' }
      ]},
      { id: 'distance', label: 'Test de marche 6 min (m)', type: 'select', options: [
        { value: 0, label: '≥ 350m (0 pt)' },
        { value: 1, label: '250-349m (1 pt)' },
        { value: 2, label: '150-249m (2 pts)' },
        { value: 3, label: '< 150m (3 pts)' }
      ]},
      { id: 'mmrc', label: 'Dyspnée mMRC', type: 'select', options: [
        { value: 0, label: '0-1 (0 pt)' },
        { value: 1, label: '2 (1 pt)' },
        { value: 2, label: '3 (2 pts)' },
        { value: 3, label: '4 (3 pts)' }
      ]},
      { id: 'bmi', label: 'IMC (kg/m²)', type: 'select', options: [
        { value: 0, label: '> 21 (0 pt)' },
        { value: 1, label: '≤ 21 (1 pt)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `BODE ${score}`, detail: 'Stade 1 - Survie 4 ans ~80%' };
      if (score <= 4) return { level: 'warning', text: `BODE ${score}`, detail: 'Stade 2 - Survie 4 ans ~67%' };
      if (score <= 6) return { level: 'danger', text: `BODE ${score}`, detail: 'Stade 3 - Survie 4 ans ~57%' };
      return { level: 'danger', text: `BODE ${score}`, detail: 'Stade 4 - Survie 4 ans ~18%' };
    },
    formula: 'BODE = BMI + Obstruction (VEMS) + Dyspnée + Exercice (6MWT)'
  },

  // BREF - Batterie Rapide Efficience Frontale
  bref: {
    name: 'Batterie BREF',
    category: 'Neurologie',
    icon: Brain,
    description: 'Évaluation fonctions cognitives frontales',
    color: 'purple',
    fields: [
      { id: 'similarities', label: 'Similitudes (banane-orange, table-chaise, tulipe-rose)', type: 'select', options: [
        { value: 0, label: '0 - Aucune similitude' },
        { value: 1, label: '1 - 1 catégorie' },
        { value: 2, label: '2 - 2 catégories' },
        { value: 3, label: '3 - 3 catégories' }
      ]},
      { id: 'fluency', label: 'Fluence lexicale (mots en S en 1 min)', type: 'select', options: [
        { value: 0, label: '0 - < 3 mots' },
        { value: 1, label: '1 - 3-5 mots' },
        { value: 2, label: '2 - 6-9 mots' },
        { value: 3, label: '3 - > 9 mots' }
      ]},
      { id: 'motor', label: 'Séquence motrice de Luria', type: 'select', options: [
        { value: 0, label: '0 - Échec' },
        { value: 1, label: '1 - 3 séquences avec aide' },
        { value: 2, label: '2 - 3 séquences sans aide' },
        { value: 3, label: '3 - 6 séquences seul' }
      ]},
      { id: 'conflict', label: 'Consignes conflictuelles (1 coup si 2, 2 coups si 1)', type: 'select', options: [
        { value: 0, label: '0 - Échec' },
        { value: 1, label: '1 - 1-2 erreurs' },
        { value: 2, label: '2 - 0-1 erreur' },
        { value: 3, label: '3 - Aucune erreur' }
      ]},
      { id: 'goNoGo', label: 'Go-No Go (1 coup si 1, rien si 2)', type: 'select', options: [
        { value: 0, label: '0 - Échec' },
        { value: 1, label: '1 - > 2 erreurs' },
        { value: 2, label: '2 - 1-2 erreurs' },
        { value: 3, label: '3 - Aucune erreur' }
      ]},
      { id: 'prehension', label: 'Comportement de préhension', type: 'select', options: [
        { value: 0, label: '0 - Prend malgré consigne' },
        { value: 1, label: '1 - Hésite puis prend' },
        { value: 2, label: '2 - Hésite mais ne prend pas' },
        { value: 3, label: '3 - Ne prend pas' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 18 };
    },
    interpret: (score) => {
      if (score >= 16) return { level: 'success', text: `BREF ${score}/18`, detail: 'Fonctions frontales normales' };
      if (score >= 12) return { level: 'warning', text: `BREF ${score}/18`, detail: 'Dysfonction frontale légère' };
      return { level: 'danger', text: `BREF ${score}/18`, detail: 'Dysfonction frontale sévère - Bilan neuropsychologique' };
    },
    formula: 'BREF: 6 épreuves (0-3). Seuil pathologique < 16'
  },

  // CAM - Confusion Assessment Method
  cam: {
    name: 'CAM (Confusion Assessment)',
    category: 'Gériatrie',
    icon: Brain,
    description: 'Détection du delirium',
    color: 'amber',
    fields: [
      { id: 'acuteOnset', label: '1. Début aigu ou fluctuation', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'Oui' }
      ]},
      { id: 'inattention', label: '2. Inattention', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'Oui' }
      ]},
      { id: 'disorganized', label: '3. Pensée désorganisée', type: 'select', options: [
        { value: 0, label: 'Non' },
        { value: 1, label: 'Oui' }
      ]},
      { id: 'alteredLoc', label: '4. Altération niveau conscience', type: 'select', options: [
        { value: 0, label: 'Non (alerte)' },
        { value: 1, label: 'Oui (hypervigilant, somnolent, stuporeux, comateux)' }
      ]}
    ],
    calculate: (values) => {
      // CAM positif si critères 1+2 ET (3 OU 4)
      const crit1 = values.acuteOnset === 1;
      const crit2 = values.inattention === 1;
      const crit3 = values.disorganized === 1;
      const crit4 = values.alteredLoc === 1;
      const positive = crit1 && crit2 && (crit3 || crit4);
      return { score: positive ? 1 : 0, max: 1, criteria: { crit1, crit2, crit3, crit4 } };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'CAM négatif', detail: 'Delirium peu probable' };
      return { level: 'danger', text: 'CAM positif', detail: 'Delirium probable - Recherche étiologie' };
    },
    formula: 'CAM + si: (1+2) ET (3 OU 4)'
  },

  // CDAI - Crohn
  cdai: {
    name: 'Score CDAI',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Activité maladie de Crohn',
    color: 'yellow',
    fields: [
      { id: 'stools', label: 'Nombre de selles liquides/molles (somme 7j)', type: 'number', min: 0, max: 100 },
      { id: 'pain', label: 'Douleur abdominale 7j (0-3 × 7j)', type: 'number', min: 0, max: 21 },
      { id: 'wellbeing', label: 'Bien-être général 7j (0-4 × 7j)', type: 'number', min: 0, max: 28 },
      { id: 'complications', label: 'Nombre complications (arthrite, iritis, érythème, fissure, fistule, abcès, fièvre)', type: 'number', min: 0, max: 10 },
      { id: 'loperamide', label: 'Prise lopéramide', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 1, label: 'Oui (30 pts)' }
      ]},
      { id: 'mass', label: 'Masse abdominale', type: 'select', options: [
        { value: 0, label: 'Non (0 pt)' },
        { value: 2, label: 'Douteuse (2 pts)' },
        { value: 5, label: 'Oui (5 pts)' }
      ]},
      { id: 'hematocrit', label: 'Hématocrite - Hte attendu (%)', type: 'number', min: -20, max: 20 },
      { id: 'weightRatio', label: '100 × (1 - Poids/Poids idéal)', type: 'number', min: -50, max: 50 }
    ],
    calculate: (values) => {
      let total = 0;
      total += (values.stools || 0) * 2;
      total += (values.pain || 0) * 5;
      total += (values.wellbeing || 0) * 7;
      total += (values.complications || 0) * 20;
      total += (values.loperamide || 0) * 30;
      total += (values.mass || 0) * 10;
      total += (values.hematocrit || 0) * 6;
      total += (values.weightRatio || 0) * 1;
      return { score: Math.round(total), max: 600 };
    },
    interpret: (score) => {
      if (score < 150) return { level: 'success', text: `CDAI ${score}`, detail: 'Rémission' };
      if (score < 220) return { level: 'warning', text: `CDAI ${score}`, detail: 'Maladie active légère' };
      if (score < 450) return { level: 'danger', text: `CDAI ${score}`, detail: 'Maladie active modérée' };
      return { level: 'danger', text: `CDAI ${score}`, detail: 'Maladie sévère' };
    },
    formula: 'CDAI = Σ(pondération × items). <150 = rémission'
  },

  // DIC - Score ISTH
  dicScore: {
    name: 'Score DIC (ISTH)',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Diagnostic coagulation intravasculaire disséminée',
    color: 'red',
    fields: [
      { id: 'platelets', label: 'Plaquettes', type: 'select', options: [
        { value: 0, label: '> 100 G/L (0 pt)' },
        { value: 1, label: '50-100 G/L (1 pt)' },
        { value: 2, label: '< 50 G/L (2 pts)' }
      ]},
      { id: 'dDimers', label: 'D-dimères ou PDF', type: 'select', options: [
        { value: 0, label: 'Pas d\'augmentation (0 pt)' },
        { value: 2, label: 'Augmentation modérée (2 pts)' },
        { value: 3, label: 'Augmentation forte (3 pts)' }
      ]},
      { id: 'pt', label: 'Allongement TP', type: 'select', options: [
        { value: 0, label: '< 3 sec (0 pt)' },
        { value: 1, label: '3-6 sec (1 pt)' },
        { value: 2, label: '> 6 sec (2 pts)' }
      ]},
      { id: 'fibrinogen', label: 'Fibrinogène', type: 'select', options: [
        { value: 0, label: '≥ 1 g/L (0 pt)' },
        { value: 1, label: '< 1 g/L (1 pt)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 8 };
    },
    interpret: (score) => {
      if (score < 5) return { level: 'success', text: `DIC ${score}`, detail: 'DIC non avérée - Répéter si suspicion' };
      return { level: 'danger', text: `DIC ${score}`, detail: 'Compatible avec DIC avérée - Traitement étiologique' };
    },
    formula: 'DIC ISTH ≥ 5 = DIC avérée'
  },

  // DLQI - Dermatologie
  dlqi: {
    name: 'Score DLQI',
    category: 'Dermatologie',
    icon: User,
    description: 'Impact dermatose sur qualité de vie',
    color: 'pink',
    fields: [
      { id: 'symptoms', label: '1. Prurit, douleur, irritation', type: 'select', options: [
        { value: 0, label: '0 - Pas du tout' }, { value: 1, label: '1 - Un peu' },
        { value: 2, label: '2 - Beaucoup' }, { value: 3, label: '3 - Énormément' }
      ]},
      { id: 'embarrassment', label: '2. Gêne, embarras', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'shopping', label: '3. Impact courses, ménage, jardinage', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'clothes', label: '4. Choix des vêtements', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'social', label: '5. Activités sociales/loisirs', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'sport', label: '6. Sport', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'work', label: '7. Travail/études', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'relationships', label: '8. Relations amicales/familiales', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'sexual', label: '9. Vie sexuelle', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'treatment', label: '10. Contraintes traitement', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 30 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `DLQI ${score}/30`, detail: 'Pas d\'impact sur la qualité de vie' };
      if (score <= 5) return { level: 'success', text: `DLQI ${score}/30`, detail: 'Impact faible' };
      if (score <= 10) return { level: 'warning', text: `DLQI ${score}/30`, detail: 'Impact modéré' };
      if (score <= 20) return { level: 'danger', text: `DLQI ${score}/30`, detail: 'Impact important' };
      return { level: 'danger', text: `DLQI ${score}/30`, detail: 'Impact très important sur la vie' };
    },
    formula: 'DLQI: 10 questions (0-3). >10 = biothérapie justifiable'
  },

  // Emboli Score (Ottawa)
  ottawa: {
    name: 'Score d\'Ottawa (Récidive MTEV)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque récidive MTEV après arrêt anticoagulation',
    color: 'rose',
    fields: [
      { id: 'male', label: 'Sexe masculin', type: 'checkbox', points: 1 },
      { id: 'dvtProximal', label: 'TVP proximale (index)', type: 'checkbox', points: 1 },
      { id: 'postThrombotic', label: 'Syndrome post-thrombotique', type: 'checkbox', points: 1 },
      { id: 'dDimers', label: 'D-dimères élevés sous AVK', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.male) total += 1;
      if (values.dvtProximal) total += 1;
      if (values.postThrombotic) total += 1;
      if (values.dDimers) total += 2;
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score <= 0) return { level: 'success', text: `Ottawa ${score}`, detail: 'Faible risque récidive ~3.4%/an - Arrêt AC possible' };
      return { level: 'danger', text: `Ottawa ${score}`, detail: `Risque récidive ~8-14%/an - AC au long cours à discuter` };
    },
    formula: 'Ottawa = Homme + TVP prox + SPT + D-dimères×2'
  },

  // FLI - Fatty Liver Index
  fli: {
    name: 'Fatty Liver Index',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Prédiction stéatose hépatique',
    color: 'yellow',
    fields: [
      { id: 'bmi', label: 'IMC (kg/m²)', type: 'number', min: 15, max: 60, step: 0.1 },
      { id: 'waist', label: 'Tour de taille (cm)', type: 'number', min: 50, max: 200 },
      { id: 'triglycerides', label: 'Triglycérides (mg/dL)', type: 'number', min: 20, max: 1000 },
      { id: 'ggt', label: 'GGT (U/L)', type: 'number', min: 5, max: 1000 }
    ],
    calculate: (values) => {
      const bmi = values.bmi || 25;
      const waist = values.waist || 90;
      const tg = values.triglycerides || 100;
      const ggt = values.ggt || 30;
      
      const e = Math.exp(0.953 * Math.log(tg) + 0.139 * bmi + 0.718 * Math.log(ggt) + 0.053 * waist - 15.745);
      const fli = (e / (1 + e)) * 100;
      return { score: fli.toFixed(0), max: 100 };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 30) return { level: 'success', text: `FLI ${score}`, detail: 'Stéatose hépatique exclue' };
      if (s < 60) return { level: 'warning', text: `FLI ${score}`, detail: 'Zone indéterminée - Échographie à discuter' };
      return { level: 'danger', text: `FLI ${score}`, detail: 'Stéatose hépatique probable - Échographie/Fibroscan' };
    },
    formula: 'FLI: formule incluant IMC, tour taille, TG, GGT'
  },

  // GAD-7 - Anxiété
  gad7: {
    name: 'GAD-7',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Évaluation trouble anxieux généralisé',
    color: 'indigo',
    fields: [
      { id: 'nervous', label: 'Se sentir nerveux ou très tendu', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - Plusieurs jours' },
        { value: 2, label: '2 - Plus de la moitié des jours' }, { value: 3, label: '3 - Presque tous les jours' }
      ]},
      { id: 'worry', label: 'Ne pas pouvoir arrêter de s\'inquiéter', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'tooMuchWorry', label: 'S\'inquiéter excessivement', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'relax', label: 'Difficultés à se détendre', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'restless', label: 'Agitation, ne pas tenir en place', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'annoyed', label: 'S\'irriter ou s\'agacer facilement', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'afraid', label: 'Peur que quelque chose de terrible arrive', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 21 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `GAD-7 ${score}`, detail: 'Anxiété minimale' };
      if (score <= 9) return { level: 'warning', text: `GAD-7 ${score}`, detail: 'Anxiété légère' };
      if (score <= 14) return { level: 'warning', text: `GAD-7 ${score}`, detail: 'Anxiété modérée - Consultation recommandée' };
      return { level: 'danger', text: `GAD-7 ${score}`, detail: 'Anxiété sévère - Prise en charge spécialisée' };
    },
    formula: 'GAD-7: 7 items (0-3). ≥10 = trouble anxieux probable'
  },

  // PHQ-9 - Dépression
  phq9: {
    name: 'PHQ-9',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Dépistage dépression',
    color: 'indigo',
    fields: [
      { id: 'interest', label: 'Peu d\'intérêt ou de plaisir', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - Plusieurs jours' },
        { value: 2, label: '2 - Plus de la moitié des jours' }, { value: 3, label: '3 - Presque tous les jours' }
      ]},
      { id: 'depressed', label: 'Triste, déprimé, désespéré', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'sleep', label: 'Difficultés de sommeil ou trop dormir', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'tired', label: 'Fatigue ou manque d\'énergie', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'appetite', label: 'Manque d\'appétit ou excès alimentaire', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'failure', label: 'Mauvaise opinion de soi', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'concentration', label: 'Difficultés de concentration', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'moving', label: 'Bouger/parler lentement ou agité', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'suicidal', label: 'Pensées suicidaires ou automutilatoires', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 27, suicidal: values.suicidal || 0 };
    },
    interpret: (score, calculated) => {
      if (calculated?.suicidal > 0) {
        return { level: 'danger', text: `PHQ-9 ${score} (item 9 positif)`, detail: 'Évaluation risque suicidaire urgente!' };
      }
      if (score <= 4) return { level: 'success', text: `PHQ-9 ${score}`, detail: 'Dépression minimale' };
      if (score <= 9) return { level: 'warning', text: `PHQ-9 ${score}`, detail: 'Dépression légère - Surveillance' };
      if (score <= 14) return { level: 'warning', text: `PHQ-9 ${score}`, detail: 'Dépression modérée - Traitement à considérer' };
      if (score <= 19) return { level: 'danger', text: `PHQ-9 ${score}`, detail: 'Dépression modérément sévère - Traitement actif' };
      return { level: 'danger', text: `PHQ-9 ${score}`, detail: 'Dépression sévère - Traitement immédiat, référer' };
    },
    formula: 'PHQ-9: 9 items (0-3). ≥10 = dépression probable'
  }
};
